'use client';

import { forwardRef, useId } from 'react';

import { css, cx } from '@/styled-system/css';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { calendarYearSelectButtonRecipe } from './styles/calendar-year-select-button.styles';
import { calendarYearSelectInputRecipe } from './styles/calendar-year-select-input.styles';
import { calendarYearSelectRecipe } from './styles/calendar-year-select.styles';
import type { CalendarYearSelectPropsInterface } from './types/calendar-year-select.types';

export const CalendarYearSelect = forwardRef<HTMLDivElement, CalendarYearSelectPropsInterface>(
  (props: CalendarYearSelectPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, disabled = false, value, onChange, ...rest } = props;
    const id = useId();
    const styles = css(calendarYearSelectRecipe.raw({ disabled }), cssProp);
    const buttonStyles = css(calendarYearSelectButtonRecipe.raw({ disabled }));
    const inputStyles = css(calendarYearSelectInputRecipe.raw({ disabled }));
    return (
      <div
        ref={forwardedRef}
        data-c11n-component="Calendar.YearSelect"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'group', styles, className)}
        {...rest}
      >
        <label htmlFor={id} className={css({ srOnly: true })}>
          Select year
        </label>
        <select id={id} onChange={onChange} value={value} className={cx('c11n', inputStyles)}>
          {children}
        </select>
        <IconButton
          icon={
            <Icon>
              <svg viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.8919 6.0608L9.1219 11.6808C8.98024 11.8797 8.75111 11.9978 8.5069 11.9978C8.26269 11.9978 8.03356 11.8797 7.8919 11.6808L4.1219 6.0608C3.98334 5.84334 3.97758 5.56677 4.10698 5.34373C4.23639 5.1207 4.47935 4.98843 4.7369 5.0008L12.2769 5.0008C12.5345 4.98843 12.7774 5.1207 12.9068 5.34373C13.0362 5.56677 13.0305 5.84334 12.8919 6.0608Z"
                  fill="currentColor"
                />
              </svg>
            </Icon>
          }
          type="button"
          emphasis="bare"
          shape="circle"
          tone="neutral"
          aria-hidden="true"
          tabIndex={-1}
          className={cx('c11n', buttonStyles)}
          title="Select year"
        />
      </div>
    );
  },
);

CalendarYearSelect.displayName = 'Calendar.YearSelect';
